/**
 * @circles/convex — what the other packages import.
 *
 * - `api`: the generated function references (typed). `internal` is deliberately
 *   not exported; only the backend calls internal functions.
 * - Types: Id/Doc from the data model, the manifest type + validators, URL helpers.
 */
export { api } from '../functions/_generated/api';
export type { Id, Doc, DataModel, TableNames } from '../functions/_generated/dataModel';

export {
  validateManifest,
  manifestValidator,
  COLLECTION_NAME_RE,
  SERVER_ENTRIES,
  type CircleManifest,
  type CollectionScope,
  type CollectionSpec,
  type WritableBy,
  type PatchableBy,
  type ManifestValidation,
  type ManifestOptions,
  type ServerEntry,
  type ServerSpec,
  type ServerScheduleSpec,
} from '../functions/lib/manifest';
export { MAX_SERVER_SCRIPT_BYTES, WORKER_COMPAT_DATE } from '../functions/lib/cloudflare';
export {
  MAX_SERVER_SOURCE_BYTES,
  MAX_SERVER_SOURCE_FILES,
  SERVER_SOURCE_DIR,
  isServerSourcePath,
  serverSourceContentType,
  serverSourcePathProblem,
} from '../functions/lib/serverSources';
export { slugify, isValidSlug, SLUG_RE } from '../functions/lib/slug';
export {
  CONNECTOR_NAME_RE,
  isValidConnectorName,
  normalizeBaseUrl,
  checkAuthHeader,
  checkSecret,
} from '../functions/lib/connectors';
export { contentTypeFor, extensionOf } from '../functions/lib/contentType';
export { appPath, parseAppPath, resolveNotificationUrl, isValidNotificationUrl } from '../functions/lib/urls';
export {
  CIRCLE_KEY_ALPHABET,
  CIRCLE_KEY_LENGTH,
  isValidCircleKey,
  formatCircleId,
  parseCircleId,
  circleMatchesId,
  findCircleEntryKey,
} from '../functions/lib/circleIds';
export type { ErrorCode, CirclesErrorData } from '../functions/lib/errors';
export { MAX_TRANSACT_OPS } from '../functions/lib/documentOps';
export {
  AI_CONNECTOR,
  AI_IMAGE_TYPES,
  AI_MODELS,
  DEFAULT_AI_MODEL,
  DEFAULT_AI_TOKENS,
  JSON_INSTRUCTION,
  MAX_AI_IMAGES,
  MAX_AI_IMAGE_BYTES,
  MAX_AI_PROMPT_BYTES,
  MAX_AI_SYSTEM_BYTES,
  MAX_AI_TOKENS,
  type AiImageType,
} from '../functions/lib/ai';
export { normalizeLoginCode, formatLoginCode, normalizeEmail, isValidEmail } from '../functions/lib/loginCodes';
export type { Every } from '../functions/lib/time';
export { zonedParts, zonedToUtc, tzOffsetMs, nextOccurrence } from '../functions/lib/time';
/** App tokens, the third principal (architecture §2): the `createdBy` of system-scoped writes and the token's lifetime. */
export { APP_ACTOR_ID, APP_TOKEN_TTL_MS, type AppTokenClaims, type AppTokenScope } from '../functions/lib/appTokens';
export type { ServerContext } from '../functions/lib/context';
export { AVATAR_PRESETS, type AvatarPresetEntry } from '../functions/lib/avatarLibrary';
export type { AvatarKind } from '../functions/lib/avatars';
export { AVATAR_HUES } from '../functions/lib/avatarHues';

/** localStorage keys shared by the launcher (writer) and the runtime SDK (reader). */
export const STORAGE_KEYS = {
  /** JSON object { [circle identifier `<slug>-<key>`; older launchers: bare slug]: { token, circleId, circleName, memberId, memberName } } */
  deviceTokens: 'circles.deviceTokens',
  /** Single most-recently-used device token (fallback for the SDK). */
  deviceToken: 'circles.deviceToken',
  /** The owner's account key on the builder page. */
  accountKey: 'circles.accountKey',
} as const;

/** Query param the service worker appends when opening an app from a push click. */
export const NOTIFICATION_QUERY_PARAM = 'circlesNotification';
